import React, { useState, useEffect, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser } from '../../actions/loginAndRegisterActions';
import CheckBox from '../../components/CheckBox';
import ErrorResponse from '../../components/ErrorResponse';
import Input from '../../components/Input';
import PrimaryButton from '../../components/PrimaryButton';
import ViewWrapper, { FormWrapper, Header1 } from '../../components/ViewWrapper';
import { EMAIL_REGEX, PASSWORD_REGEX, linkToTermsOfUse } from '../../constants';
import logo from '../../img/Airthings_Favicon.png';
import { Store } from '../../reducers';
import { RequestType } from '../../reducers/requestReducer';
import InviteInfo from '../invite/InviteInfo';
import styles from './RegisterView.module.scss';

const RegisterView = (): JSX.Element => {
    const { t: txt } = useTranslation();
    const dispatch = useDispatch();
    const { invite } = useSelector((state: Store) => state.invite);
    const { loading, error } = useSelector((state: Store) => state.requests[RequestType.RegisterUser]);

    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [validateInput, setValidateInput] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [confirmedPassword, setConfirmedPassword] = useState('');

    useEffect(() => {
        if (invite) {
            setEmail(invite.email);
            setUserName(invite.name);
        }
    }, [invite]);

    const onChangeAgreeTerms = (): void => {
        setAgreedToTerms(!agreedToTerms);
    };

    const onChange = (value: string, key: string): void => {
        if (key === 'userName') {
            setUserName(value);
        } else if (key === 'email') {
            setEmail(value);
        } else if (key === 'password') {
            setPassword(value.trim());
        } else if (key === 'confirmPassword') {
            setConfirmedPassword(value.trim());
        }
    };

    const inputIsValid = (): boolean => {
        const userInputValid = userName.trim().length > 0 && EMAIL_REGEX.test(email);
        const passwordAndTermsValid = PASSWORD_REGEX.test(password) && confirmedPassword === password && agreedToTerms;
        return passwordAndTermsValid && (!!invite || userInputValid);
    };

    const onClickRegister = (e: SyntheticEvent<HTMLButtonElement> | SyntheticEvent<HTMLFormElement>): void => {
        e.preventDefault();
        if (inputIsValid()) {
            dispatch(registerUser({ name: userName, email, password }));
        } else {
            setValidateInput(true);
        }
    };

    return (
        <ViewWrapper>
            <FormWrapper onSubmit={onClickRegister} testAttr="registration-form">
                {invite ? (
                    <InviteInfo invite={invite} userRegistered={false} />
                ) : (
                    <>
                        <img src={logo} className={styles.logo} alt={txt('Logo.AirthingsLogo')} />
                        <Header1>{txt('Register.NewAccount')}</Header1>
                        <Input
                            label="User.Name"
                            id="userName"
                            maxLength={199}
                            autoComplete="name"
                            invalid={validateInput && userName.trim().length < 1}
                            onChange={onChange}
                            value={userName}
                            required
                            leadingIcon="person"
                        />
                        <Input
                            label="User.Email"
                            type="email"
                            id="email"
                            required
                            autoComplete="email"
                            invalid={validateInput && !EMAIL_REGEX.test(email)}
                            onChange={onChange}
                            value={email}
                            leadingIcon="email"
                        />
                    </>
                )}
                <Input
                    label="User.Password"
                    type="password"
                    id="password"
                    required
                    invalid={validateInput && !PASSWORD_REGEX.test(password)}
                    helpText="Register.PasswordHint"
                    alwaysShowHelpText
                    autoComplete="new-password"
                    onChange={onChange}
                    value={password}
                />
                <Input
                    label="Register.ReEnterPassword"
                    type="password"
                    id="confirmPassword"
                    required
                    helpText="Register.PasswordConfirmHint"
                    autoComplete="new-password"
                    invalid={validateInput && password !== confirmedPassword}
                    onChange={onChange}
                    value={confirmedPassword}
                />
                <CheckBox
                    label="Register.AgreeToTerms"
                    id="agreeToTerms"
                    checked={agreedToTerms}
                    onChange={onChangeAgreeTerms}
                    link={linkToTermsOfUse}
                    linkText="Register.AgreeToTermsLinkText"
                    invalid={validateInput && !agreedToTerms}
                    helpText="Register.AgreeToTermsHint"
                    testAttr="agree-terms"
                />
                {error && <ErrorResponse error={`ErrorCodes.${error.error}`} testAttr={'register-user-error'} />}
                <PrimaryButton
                    type="submit"
                    loading={loading}
                    title="Register.CreateAccount"
                    onClick={onClickRegister}
                    testAttr="create-account"
                />
            </FormWrapper>
        </ViewWrapper>
    );
};

export default RegisterView;
